import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Paperclip, X, Eye, Download, Info } from "lucide-react";
import { useBreakpoint } from "../hooks/useBreakpoint";
import Pagination from "./Pagination";
import "./ResponsiveTableCards.css";

/**
 * Responsive wrapper that renders the existing table on wide screens
 * and a card list + detail drawer on tablets/phones.
 */
export default function ResponsiveTableCards({
  breakpoint = 1024,
  renderTable,
  data = [],
  loading = false,
  emptyTitle = "No records",
  emptySub = "Nothing to show right now.",
  listTitle,
  listSubtitle,
  cardMapper,
  drawerMapper,
  onCardClick,
  pagination, // { page, totalPages, totalRecords, pageSize, currentPageCount, onPageChange }
}) {
  const isNarrow = useBreakpoint(breakpoint);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState(null);

  const cards = useMemo(
    () =>
      data
        .map((row) => {
          const mapped = cardMapper ? cardMapper(row) : null;
          return mapped ? { ...mapped, _row: row } : null;
        })
        .filter(Boolean),
    [data, cardMapper],
  );

  const openDrawer = (row, cardActions) => {
    if (!drawerMapper) return;
    const mapped = drawerMapper(row);
    setDrawerData({ ...mapped, _cardActions: cardActions || [] });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerData(null);
  };

  if (!isNarrow) {
    return renderTable ? renderTable() : null;
  }

  return (
    <div className="rtc-wrapper">
      <div className="rtc-card-shell">
        {(listTitle || listSubtitle) && (
          <>
            <div className="rtc-header">
              <div>{listTitle}</div>
            </div>
            {listSubtitle ? <div className="rtc-sub">{listSubtitle}</div> : null}
          </>
        )}

        {loading ? (
          <div className="rtc-loader">
            <div className="rtc-spinner" />
            Loading...
          </div>
        ) : cards.length === 0 ? (
          <div className="rtc-empty">
            <div>{emptyTitle}</div>
            <div style={{ marginTop: 4 }}>{emptySub}</div>
          </div>
        ) : (
          <div className="rtc-list">
            {cards.map((card) => (
              <CardItem
                key={card.id}
                card={card}
                hasDrawer={!!drawerMapper && !onCardClick}
                onOpen={() => openDrawer(card._row, card.actions)}
                onCardClick={
                  onCardClick ? () => onCardClick(card._row) : undefined
                }
              />
            ))}
          </div>
        )}

        {pagination && cards.length > 0 && (
          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages || 1}
            totalRecords={pagination.totalRecords}
            pageSize={pagination.pageSize}
            currentPageCount={pagination.currentPageCount ?? cards.length}
            onPageChange={pagination.onPageChange}
            compact
            className="rtc-pagination"
          />
        )}
      </div>

      {drawerOpen && drawerData && (
        <DetailDrawer data={drawerData} onClose={closeDrawer} />
      )}
    </div>
  );
}

function CardItem({ card, hasDrawer, onOpen, onCardClick }) {
  return (
    <div className="rtc-card">
      <div className="rtc-card-content">
        <div className={`rtc-icon ${card.tone || ""}`}>{card.icon}</div>

        <div className="rtc-body">
          <div className="rtc-title-row">
            <div>
              <div className="rtc-title">{card.title}</div>
              {card.subtitle ? (
                <div className="rtc-subtitle">{card.subtitle}</div>
              ) : null}
            </div>
          </div>

          {card.meta?.length ? (
            <div className="rtc-meta">
              {card.meta.map((m, idx) => (
                <div className="rtc-meta-item" key={idx}>
                  <span className="rtc-meta-label">{m.label}</span>
                  <span className="rtc-meta-value">{m.value}</span>
                </div>
              ))}
            </div>
          ) : null}

          {card.badges?.length ? (
            <div className="rtc-badges">
              {card.badges.map((b, idx) => (
                <span className={`rtc-badge ${b.tone || ""}`} key={idx}>
                  {b.label && <>{b.label}: </>}
                  {b.value}
                </span>
              ))}
            </div>
          ) : null}

          {typeof card.attachmentsCount === "number" ? (
            <div className="rtc-attachment">
              <Paperclip size={14} />
              {card.attachmentsLabel ||
                `${card.attachmentsCount} attachment${
                  card.attachmentsCount === 1 ? "" : "s"
                }`}
            </div>
          ) : null}
        </div>

        {card.actions?.length ? (
          <div className="rtc-actions">
            {card.actions.map((action, idx) => (
              <button
                key={idx}
                className={`rtc-action-btn ${action.variant || ""}`}
                onClick={action.onClick}
                title={action.label}
              >
                {action.icon || action.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {onCardClick ? (
        <div className="rtc-card-footer">
          <button
            type="button"
            className="rtc-view-more-btn"
            onClick={onCardClick}
            aria-label={`View documents for ${card.title}`}
          >
            <Info size={14} />
            View documents
          </button>
        </div>
      ) : hasDrawer ? (
        <div className="rtc-card-footer">
          <button
            type="button"
            className="rtc-view-more-btn"
            onClick={onOpen}
            aria-label={`View more details for ${card.title}`}
          >
            <Info size={14} />
            View more
          </button>
        </div>
      ) : null}
    </div>
  );
}

const DRAWER_TRANSITION_MS = 320;

function DetailDrawer({ data, onClose }) {
  const [isOpen, setIsOpen] = useState(false);
  const isClosingRef = useRef(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setIsOpen(true));
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  const handleClose = useCallback(() => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    setIsOpen(false);
    window.setTimeout(onClose, DRAWER_TRANSITION_MS);
  }, [onClose]);

  return (
    <div
      className={`rtc-drawer-overlay${isOpen ? " rtc-drawer-overlay--open" : ""}`}
      role="dialog"
      aria-modal="true"
      onClick={handleClose}
    >
      <div
        className={`rtc-drawer scrollable${isOpen ? " rtc-drawer--open" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rtc-drawer-header">
          <div className="rtc-drawer-title">{data.title}</div>
          <button className="rtc-close-btn" onClick={handleClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {data.fields?.length ? (
          <div className="rtc-drawer-fields">
            {data.fields.map((f, idx) => (
              <div key={idx}>
                <div className="rtc-field-label">{f.label}</div>
                <div className="rtc-field-value">{f.value}</div>
              </div>
            ))}
          </div>
        ) : null}

        {data.attachments?.length ? (
          <div className="rtc-attachments">
            {data.attachments.map((att, idx) => (
              <div className="rtc-attachment-row" key={idx}>
                <div className="rtc-attachment-name">{att.label}</div>

                <div className="rtc-attachment-actions">
                  {att.onPreview ? (
                    <button
                      className="rtc-chip secondary"
                      onClick={() => { handleClose(); att.onPreview(); }}
                      aria-label={`Preview ${att.label}`}
                    >
                      <Eye size={16} />
                    </button>
                  ) : null}

                  {att.onDownload ? (
                    <button
                      className="rtc-chip secondary"
                      onClick={() => { handleClose(); att.onDownload(); }}
                      aria-label={`Download ${att.label}`}
                    >
                      <Download size={16} />
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {data._cardActions?.length ? (
          <div className="rtc-drawer-actions">
            {data._cardActions.map((action, idx) => (
              <button
                key={idx}
                className={`rtc-drawer-action-btn ${action.variant || ""}`}
                onClick={() => { handleClose(); action.onClick(); }}
              >
                {action.icon && (
                  <span className="rtc-drawer-action-icon">{action.icon}</span>
                )}
                {action.label}
              </button>
            ))}
          </div>
        ) : data.primaryAction ? (
          <button
            className="rtc-primary-action"
            onClick={() => { handleClose(); data.primaryAction.onClick(); }}
          >
            {data.primaryAction.label}
          </button>
        ) : null}
      </div>
    </div>
  );
}
